import api from './api';
import { captureSession, assertCurrentSession } from '../utils/authSession';
export const resourceOperationId = () => crypto.randomUUID();
const segment = (id) => encodeURIComponent(id);
const scoped = (distributorId) => (distributorId ? { params: { distributorId } } : {});
async function request(method, url, data, options = {}) {
  const session = captureSession();
  const response = await api.request({ method, url, data, ...options, sessionBinding: session });
  assertCurrentSession(session);
  return options.responseType === 'blob' ? response : (response.data?.data ?? response.data);
}
export function verifiedResourceBlob(response, expected = {}) {
  const blob = response.data;
  if (
    (expected.sha256 && response.headers?.['x-resource-sha256'] !== expected.sha256) ||
    (expected.revision !== undefined &&
      String(response.headers?.['x-resource-revision']) !== String(expected.revision))
  ) {
    const error = new Error('Resource changed');
    error.code = 'resource_changed';
    throw error;
  }
  if (
    !(blob instanceof Blob) ||
    !['application/pdf', 'image/png', 'image/jpeg'].includes(blob.type) ||
    blob.size < 1 ||
    blob.size > 5242880 ||
    (expected.mimeType && blob.type !== expected.mimeType) ||
    (expected.fileBytes && blob.size !== expected.fileBytes)
  ) {
    throw new Error('Resource file unavailable');
  }
  return blob;
}
export const resourceError = (error) =>
  error?.code === 'resource_changed'
    ? 'This document changed. Reload it before continuing.'
    : {
        400: 'Check the document details and try again.',
        401: 'Your session ended. Sign in again.',
        403: 'You do not have access to this action.',
        404: 'This document is no longer available. Refresh the library.',
        409: 'This document changed. Reload it before continuing.',
        413: 'Choose a file no larger than 5 MB.',
        415: 'Choose a supported static PDF, PNG or JPEG file.',
        429: 'Uploads are busy. Wait a moment and retry.',
        503: 'Resources are temporarily unavailable. Please retry.',
      }[error?.response?.status] || 'The request could not be confirmed. Please retry.';
const service = {
  config: (distributorId) => request('get', '/resources/config', undefined, scoped(distributorId)),
  search: (body, admin = false, distributorId) =>
    request('post', `${admin ? '/admin' : ''}/resources/search`, body, scoped(distributorId)),
  get: (id, admin = false, distributorId) =>
    request(
      'get',
      `${admin ? '/admin' : ''}/resources/${segment(id)}`,
      undefined,
      scoped(distributorId)
    ),
  upload: (file, clientUploadId, onProgress, distributorId) => {
    const form = new FormData();
    form.append('file', file);
    form.append('clientUploadId', clientUploadId);
    return request('post', '/admin/resources/uploads', form, {
      ...scoped(distributorId),
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) =>
        onProgress?.(e.total ? Math.min(99, Math.round((e.loaded * 100) / e.total)) : 0),
    });
  },
  save: (id, body, distributorId) =>
    request(
      id ? 'put' : 'post',
      `/admin/resources${id ? `/${segment(id)}` : ''}`,
      body,
      scoped(distributorId)
    ),
  preview: (id, snapshotId, distributorId) =>
    request('get', `/admin/resources/${segment(id)}/preview`, undefined, {
      ...scoped(distributorId),
      responseType: 'blob',
      headers: { 'If-Match': snapshotId },
    }),
  file: (id, distributorId) =>
    request('get', `/resources/${segment(id)}/file`, undefined, {
      ...scoped(distributorId),
      responseType: 'blob',
    }),
  publish: (id, body, distributorId) =>
    request('post', `/admin/resources/${segment(id)}/publish`, body, scoped(distributorId)),
  archive: (id, body, distributorId) =>
    request('post', `/admin/resources/${segment(id)}/archive`, body, scoped(distributorId)),
  restore: (id, body, distributorId) =>
    request('post', `/admin/resources/${segment(id)}/restore`, body, scoped(distributorId)),
  catalog: (distributorId) =>
    request('post', '/resources/sizing/catalog', {}, scoped(distributorId)),
  evaluateArea: (body, distributorId) =>
    request('post', '/resources/sizing/evaluate-area', body, scoped(distributorId)),
  getSizingPreferences: (distributorId) =>
    request('get', '/resources/sizing/preferences', undefined, scoped(distributorId)),
  saveSizingPreferences: (body, distributorId) =>
    request('put', '/resources/sizing/preferences', body, scoped(distributorId)),
  evaluate: (body, distributorId) =>
    request('post', '/resources/sizing/evaluate', body, scoped(distributorId)),
};
export default service;
